/*
 * /classes/:slug/portfolio — chọn những gì hiển thị công khai trên trang portfolio
 * (badge, 4F Reflection, bài mới nhất của từng product). Mặc định mọi thứ đều ẩn.
 */
import { useEffect, useState, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Icon, type AnyIconName } from "@/components/icon"
import { EmptyState, ErrorState, Page, PageHeader, SectionTitle, StatusNote, usePageTitle } from "@/components/page"
import { useClass } from "@/app/ClassLayout"
import { api, post } from "@/lib/api"
import { productStatusLabel } from "./submissions/shared"

function ToggleRow({
  icon,
  title,
  note,
  checked,
  disabled,
  label,
  onChange,
}: {
  icon: AnyIconName
  title: ReactNode
  note: ReactNode
  checked: boolean
  disabled: boolean
  label: string
  onChange: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-4 rounded-2xl p-3 transition-colors hover:bg-paper">
      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-soft">
        <Icon name={icon} className="size-5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <strong className="text-base break-words text-ink">{title}</strong>
        <span className="text-sm text-muted-foreground">{note}</span>
      </span>
      <Switch checked={checked} disabled={disabled} aria-label={label} onCheckedChange={onChange} />
    </label>
  )
}

export default function PortfolioPage() {
  usePageTitle("Portfolio", "Chọn những gì con muốn hiển thị công khai trên portfolio.")
  const { slug } = useClass()
  const [data, setData] = useState<any>()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [copied, setCopied] = useState(false)

  const load = () =>
    api(`/api/classes/${encodeURIComponent(slug)}/portfolio`)
      .then(setData)
      .catch((e: any) => setError(e.message))
  useEffect(() => {
    load()
  }, [slug])

  const save = async (patch: any) => {
    setBusy(true)
    setMessage("")
    try {
      await post(`/api/classes/${encodeURIComponent(slug)}/portfolio`, patch)
      await load()
    } catch (e: any) {
      setMessage(e.message)
    } finally {
      setBusy(false)
    }
  }
  const copyLink = () => {
    navigator.clipboard
      ?.writeText(data.public_url)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {})
  }

  const header = <PageHeader eyebrow="Portfolio" title="Portfolio của con" description="Chọn những gì con muốn hiển thị công khai. Mặc định mọi thứ đều ẩn." />
  if (error)
    return (
      <Page className="max-w-3xl">
        {header}
        <ErrorState
          message={error}
          onRetry={() => {
            setError("")
            load()
          }}
        />
      </Page>
    )
  if (!data)
    return (
      <Page className="max-w-3xl">
        {header}
        <Skeleton className="h-24 rounded-[20px]" />
        <Skeleton className="h-40 rounded-[20px]" />
      </Page>
    )

  const products: any[] = data.products || []
  return (
    <Page className="max-w-3xl">
      {header}

      <Card className="border-orange bg-soft">
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-card">
            <Icon name="globe" className="size-6 text-orange" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-sm font-bold tracking-[0.08em] text-orange-strong uppercase">Trang công khai</p>
            <a href={data.public_url} target="_blank" rel="noreferrer" className="truncate font-semibold text-ink underline-offset-4 hover:underline">
              {data.public_url}
            </a>
          </div>
          <Button variant="outline" onClick={copyLink}>
            <Icon name={copied ? "check" : "share"} className="size-4" />
            {copied ? "Đã sao chép" : "Sao chép link"}
          </Button>
        </CardContent>
      </Card>

      <StatusNote tone="error">{message}</StatusNote>

      <Card>
        <CardContent className="flex flex-col gap-1">
          <ToggleRow
            icon="award"
            title="Hiển thị toàn bộ badge"
            note={`${(data.badges || []).length} badge đã nhận`}
            checked={!!data.badges_public}
            disabled={busy}
            label="Ẩn/hiện toàn bộ badge"
            onChange={() => save({ badges_public: !data.badges_public })}
          />
          <ToggleRow
            icon="notebook"
            title="Hiển thị 4F Reflection"
            note={`${data.reflection_count || 0} reflection đã nộp`}
            checked={!!data.reflections_public}
            disabled={busy}
            label="Ẩn/hiện 4F Reflection"
            onChange={() => save({ reflections_public: !data.reflections_public })}
          />
        </CardContent>
      </Card>

      <SectionTitle title="Submission mới nhất của mỗi product" />
      {products.length ? (
        <Card>
          <CardContent className="flex flex-col gap-1">
            {products.map((product) => (
              <ToggleRow
                key={product.product_id}
                icon="layers"
                title={product.product_title || product.name}
                note={
                  <>
                    {product.product_title && <>{product.name} · </>}Phiên bản {product.submission?.version} ·{" "}
                    {productStatusLabel[product.submission?.status] || product.submission?.status}
                  </>
                }
                checked={!!product.visible}
                disabled={busy}
                label={`Ẩn/hiện ${product.name}`}
                onChange={() => save({ product_visibility: { [product.product_id]: !product.visible } })}
              />
            ))}
          </CardContent>
        </Card>
      ) : (
        <EmptyState mascot="lion-thinking" title="Con chưa có bài nộp nào để hiển thị" description="Hãy nộp bài ở mục Products trước nhé." />
      )}
    </Page>
  )
}
